import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@fleet-os/ui";
import { api } from "../../api";
import "./training.css";

// Inline SVG icons (subset of Lucide) — matches the ISD/CSD sidebar convention.
const ICONS = {
  back: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>,
  clipboard: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" /></svg>,
  users: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  inbox: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
  pen: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
  chart: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="7" /><rect x="12" y="6" width="3" height="11" /><rect x="17" y="13" width="3" height="4" /></svg>,
  history: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></svg>,
  refresh: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>,
};

const NAV_ITEMS = [
  { key: "mandatory", icon: "clipboard", label: "Mandatory training" },
  { key: "requests", icon: "inbox", label: "Training requests" },
  { key: "people", icon: "users", label: "Per-person records" },
  { key: "authoring", icon: "pen", label: "Course authoring" },
  { key: "kpi", icon: "chart", label: "KPI panel" },
  { key: "audit", icon: "history", label: "Activity log" },
];

const TITLES = {
  mandatory: "Mandatory training", people: "Per-person records", requests: "Training requests",
  authoring: "Course authoring", kpi: "KPI panel", audit: "Activity log",
};

export default function TrainingLayout() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState("mandatory");
  const [syncing, setSyncing] = useState(false);
  const [banner, setBanner] = useState(null);

  const load = useCallback(() => {
    api.state().then(setState).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSync = async () => {
    setSyncing(true);
    setBanner(null);
    try {
      const res = await api.sync();
      if (res.state) setState(res.state);
      setBanner({
        type: res.errors && res.errors.length ? "error" : "ok",
        text: res.errors && res.errors.length ? `${res.toast} — ${res.errors.join("; ")}` : res.toast,
      });
      setTimeout(() => setBanner(null), 3000);
    } catch (e) {
      setBanner({ type: "error", text: "Sync failed: " + e.message });
      setTimeout(() => setBanner(null), 3000);
    } finally {
      setSyncing(false);
    }
  };

  if (error) {
    return (
      <div className="training-root training-layout">
        <div className="training-error">Failed to load training data. Please ensure the backend is running.</div>
      </div>
    );
  }

  return (
    <div className="training-root training-layout">
      <aside className="training-sidebar">
        <div className="training-sidebar-brand">
          <button className="training-sidebar-back" onClick={() => navigate("/")} aria-label="Back to Fleet OS home">
            {ICONS.back}
          </button>
          <div className="training-sidebar-brand-info">
            <span className="training-sidebar-brand-icon">TR</span>
            <div>
              <span className="training-sidebar-brand-name">Training</span>
              <span className="training-sidebar-brand-sub">Fleet OS Dashboard</span>
            </div>
          </div>
        </div>

        <nav className="training-sidebar-nav">
          <div className="training-nav-section">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`training-nav-item ${view === item.key ? "active" : ""}`}
                onClick={() => setView(item.key)}
              >
                <span className="training-nav-icon">{ICONS[item.icon]}</span>
                {item.label}
                {['mandatory', 'requests', 'people', 'authoring'].includes(item.key) && state?.[item.key]?.length > 0 && (
                  <span className="nav-badge">{state[item.key].length}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        <div className="training-sidebar-footer">
          <button className="sidebar-btn-outline"><Icon name="plus" size={14} /> Add Apps</button>
          <button className="sidebar-btn-outline"><Icon name="plus" size={14} /> Add Section</button>
          <div style={{ marginTop: '10px' }} />
          <div className="tr-user-dropdown" style={{ marginBottom: '10px' }}>
            <Avatar who="Cait Burton" size={32} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>Cait Burton</span>
              <span style={{ fontSize: '0.65rem', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>Training team</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}><Icon name="moon" size={14} /> Dark mode</span>
            <ThemeToggle />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon name="settings" size={14} /> Settings
          </div>
        </div>
      </aside>

      <main className="training-main" style={{ padding: 0 }}>
        {!state ? (
          <div className="training-loading">Loading Training Dashboard…</div>
        ) : (
          <>
            <div className="tr-topbar">
              <div className="tr-topbar-left">
                <div className="tr-breadcrumbs">Dashboard &gt; <strong>Training</strong></div>
                <input type="text" className="tr-search" placeholder="Search mandatory training..." />
              </div>
              <div className="tr-topbar-right">
                <div className="tr-user-dropdown">
                  <Avatar who="Cait Burton" size={24} /> <span style={{ color: 'var(--text-primary)' }}>Cait Burton</span> <Icon name="chevDown" size={14} />
                </div>
                <button className={`tr-sync-btn-top ${syncing ? "spinning" : ""}`} onClick={onSync} disabled={syncing}>
                  <Icon name="refresh" size={14} /> {syncing ? "Syncing…" : "Sync Moodle"}
                </button>
                <div className="tr-topbar-actions">
                  <button className="tr-topbar-btn"><Icon name="history" size={16} /></button>
                  <button className="tr-topbar-btn"><Icon name="bell" size={16} /></button>
                </div>
              </div>
            </div>
            
            <div className="tr-topbar-meta">
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--green)', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                <span style={{ width: 6, height: 6, background: 'var(--green)', borderRadius: '50%' }} /> Moodle live
              </span>
              <span><Icon name="clock" size={11} /> Updated 18 Jul, 02:45</span>
            </div>

            <OverviewTiles state={state} onTileClick={(key) => {
              const tileToView = { overdue: 'mandatory', below: 'kpi', reminders: 'mandatory', ageing: 'requests' };
              if (tileToView[key]) setView(tileToView[key]);
            }} />

            <div style={{ padding: '0 24px 48px' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold" style={{ fontSize: '1.1rem', color: 'var(--text-primary)' }}>{TITLES[view]}</h2>
                  <span className="text-xs text-muted" style={{ background: 'var(--bg-glass)', padding: '4px 8px', borderRadius: '99px' }}><Icon name="refresh" size={11} /> Moodle · updated 18 Jul, 02:45</span>
                </div>
                {view === 'mandatory' && <span className="text-xs text-muted font-semibold">Target 90% by deadline</span>}
              </div>
              
              {banner && <div className={`tr-banner tr-banner--${banner.type}`}>{banner.text}</div>}

              {view === "mandatory" && <MandatoryView mandatory={state.mandatory} />}
              {view === "people" && <PeopleView people={state.people} />}
              {view === "requests" && <RequestsView requests={state.requests} />}
              {view === "authoring" && <AuthoringView authoring={state.authoring} />}
              {view === "kpi" && <KpiView kpis={state.kpis} />}
              {view === "audit" && <AuditView audit={state.audit} />}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// --- Helper Components ---
const SVG_PATHS = {
  alert: '<path d="M12 9v4M12 17h.01"/><path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z"/>',
  bell: '<path d="M10 5a2 2 0 1 1 4 0 7 7 0 0 1 4 6v3a4 4 0 0 0 2 3H4a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/>',
  check: '<path d="m5 12 5 5 9-10"/>',
  send: '<path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/>',
  users: '<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5.5a3 3 0 0 1 0 5M21 20a6 6 0 0 0-3.5-5.4"/>',
  message: '<path d="M4 5h16v11H9l-4 3v-3H4z"/>',
  star: '<path d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.9 6.6 19.5l1.2-6-4.5-4.2 6.1-.7z"/>',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 4v4h4"/><path d="M12 8v4l3 2"/>',
  pen: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>',
  ext: '<path d="M14 4h6v6"/><path d="M10 14 20 4"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/>',
  file: '<path d="M14 3v5h5"/><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M9 13h6M9 17h6"/>',
  arrowRight: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  pin: '<circle cx="12" cy="10" r="3"/><path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z"/>',
  chart: '<path d="M4 19V5M4 19h16"/><path d="M8 16v-3M12 16V8M16 16v-5"/>',
  checkCircle: '<circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 4.5-5"/>',
  chevDown: '<path d="m6 9 6 6 6-6"/>',
  chevRight: '<path d="m9 6 6 6-6 6"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8 2 2 0 1 1-2.8 2.8 1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0 1.6 1.6 0 0 0-2.7-1.1 2 2 0 1 1-2.8-2.8A1.6 1.6 0 0 0 4.6 15a2 2 0 1 1 0-4 1.6 1.6 0 0 0 1.1-2.7 2 2 0 1 1 2.8-2.8A1.6 1.6 0 0 0 12 4.4a2 2 0 1 1 4 0 1.6 1.6 0 0 0 2.7 1.1 2 2 0 1 1 2.8 2.8A1.6 1.6 0 0 0 19.4 11a2 2 0 1 1 0 4Z"/>',
  moon: '<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a7 7 0 1 0 10.5 10.5Z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l2.5 2"/>',
};

function Icon({ name, size = 18 }) {
  if (!SVG_PATHS[name]) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: SVG_PATHS[name] }} />
  );
}

function Tag({ tone, icon, children }) {
  return (
    <span className={`tag tag-${tone || 'neutral'}`}>
      {icon && <Icon name={icon} size={11} />}
      {children}
    </span>
  );
}

function StatusBadge({ s }) {
  return (
    <span className={`badge ${s.cls}`}>
      <span className="dot" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
}

const PALETTE = ['#a8eba8', '#93c5fd', '#c4b5fd', '#fca5a5', '#fbbf24', '#5eead4'];
function Avatar({ who, size = 34 }) {
  const init = (who || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
  let n = 0;
  for (const c of init) n += c.charCodeAt(0);
  const bg = PALETTE[n % PALETTE.length];
  return <span className="avatar" style={{ width: size, height: size, background: bg, fontSize: size * 0.4 }}>{init}</span>;
}

function Meter({ pct }) {
  return (
    <div className="meter">
      <div className="meter-fill" style={{ width: `${pct}%`, background: pct >= 90 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444' }} />
      <div className="meter-target" style={{ left: '90%' }} />
    </div>
  );
}

function OverviewTiles({ state, onTileClick }) {
  let overdue = 0;
  let below = 0;
  let reminders = 0;
  
  if (state.mandatory) {
    state.mandatory.forEach(c => {
      const s = courseState(c);
      if (s.overdue) overdue += s.outstanding;
      if (s.below) below++;
      if (s.reminderDue) reminders++;
    });
  }
  
  let ageing = 0;
  if (state.requests) {
    state.requests.forEach(r => {
      if (r.stage === 'New' || r.stage === 'Planned') ageing++;
    });
  }

  const tiles = [
    { key: 'overdue', label: 'Overdue training', value: overdue, sub: 'outstanding, past deadline', icon: 'alert' },
    { key: 'below', label: 'Courses below target', value: below, sub: 'under 90%', icon: 'chart' },
    { key: 'reminders', label: 'Reminders due', value: reminders, sub: 'all clear', icon: 'bell' },
    { key: 'ageing', label: 'Ageing requests', value: ageing, sub: 'no session booked', icon: 'clock' },
  ];

  return (
    <div className="tr-tiles">
      {tiles.map(t => (
        <div key={t.key} className="tr-tile" style={{ cursor: 'pointer' }} onClick={() => typeof onTileClick === 'function' && onTileClick(t.key)}>
          <div className="tr-tile-header">
            <Icon name={t.icon} size={16} />
            <Icon name="chevRight" size={16} />
          </div>
          <div className="tr-tile-value">{t.value}</div>
          <div className="tr-tile-label">{t.label}</div>
          {t.value > 0 ? (
            <div className={`tr-tile-sub ${t.key === 'overdue' || t.key === 'ageing' ? 'alert' : 'warn'}`}>
              <Icon name="alert" size={11} /> {t.sub}
            </div>
          ) : (
            <div className="tr-tile-sub ok">
              <Icon name="check" size={11} /> all clear
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// --- Helpers ---
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysUntil = (d) => Math.round((new Date(d) - new Date('2026-06-30T00:00:00Z')) / 86400000); // Fixed date for mock data

function courseState(c) {
  const pct = c.required ? Math.min(100, Math.round((c.completed / c.required) * 100)) : 0;
  const d = daysUntil(c.deadline);
  const outstanding = Math.max(0, (c.required || 0) - (c.completed || 0));
  const complete = outstanding <= 0;
  return {
    pct, d, outstanding, complete,
    overdue: !complete && d < 0,
    below: !complete && pct < 90,
    reminderDue: !complete && d >= 0 && ((d <= 2) || (d > 2)) // Simplified for mockup
  };
}

function statusForCourse(s) {
  if (s.complete) return { label: 'Complete', cls: 'badge-green', dot: '#10b981' };
  if (s.overdue) return { label: `Overdue ${-s.d}d`, cls: 'badge-danger', dot: '#ef4444' };
  return { label: `Due in ${s.d}d`, cls: s.d <= 2 ? 'badge-warn' : 'badge-green', dot: s.d <= 2 ? '#f59e0b' : '#10b981' };
}

// --- Views ---
function MandatoryView({ mandatory }) {
  if (!mandatory?.length) return <div className="tr-card"><div className="tr-empty">No mandatory training found. Hit “Sync Moodle + Jira” to pull live data.</div></div>;
  return (
    <div className="list-shell">
      {mandatory.map((c) => {
        const s = courseState(c);
        return (
          <div key={c.id} className="list-row tr-card flex justify-between" style={{ marginBottom: '8px', border: '1px solid var(--border-subtle)', padding: '0.9rem 1.1rem' }}>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{c.name}</span>
                <Tag tone="neutral">{c.audience || "—"}</Tag>
                <StatusBadge s={statusForCourse(s)} />
                {s.below && <Tag tone="warn" icon="alert">Below target</Tag>}
                {s.reminderDue && <Tag tone="accent" icon="bell">Reminder due</Tag>}
              </div>
              <div className="flex items-center gap-3 mt-2" style={{ maxWidth: '32rem' }}>
                <div className="flex-1"><Meter pct={s.pct} /></div>
                <span className="text-xs font-semibold text-right" style={{ width: '2.5rem' }}>{s.pct}%</span>
                <span className="text-xs text-muted whitespace-nowrap">{c.completed}/{c.required} · {s.outstanding} outstanding</span>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-muted">Reminders: <span style={{ background: 'var(--bg-glass)', padding: '2px 6px', borderRadius: '4px' }}>No reminder sent</span></span>
                <span className="text-xs text-muted">Deadline {fmtDate(c.deadline)}</span>
              </div>
            </div>
            
            <div className="flex flex-col items-end justify-between ml-4 shrink-0">
              <a href="#" className="source-link" onClick={e => e.preventDefault()}>Moodle <Icon name="ext" size={11} /></a>
              <div className="row-actions mt-3">
                <button className="btn-action"><Icon name="file" size={12} /> Preview</button>
                <button className="btn-action btn-action-primary"><Icon name="send" size={12} /> Remind</button>
                <button className="btn-action"><Icon name="users" size={12} /> Push list</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PeopleView({ people }) {
  if (!people?.length) return <div className="tr-card"><div className="tr-empty">No people data found.</div></div>;
  return (
    <>
      <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#60a5fa' }}>
        <Icon name="lock" size={14} />
        <span>Training records are private to the person, their manager and the Training team (NFR-6).</span>
      </div>
      <div className="list-shell">
        {people.map((e) => {
          // Parse data to handle both mock values and real Moodle array data
          const completedCount = Array.isArray(e.completed) ? e.completed.length : (e.completed || 0);
          const outstandingCount = Array.isArray(e.outstanding) ? e.outstanding.length : (e.outstanding !== undefined ? e.outstanding : 1);
          
          let overdueCount = 0;
          if (Array.isArray(e.outstanding)) {
            const today = new Date('2026-06-30T00:00:00Z'); // consistent with daysUntil mock date for demo
            overdueCount = e.outstanding.filter(o => o.due && new Date(o.due) < today).length;
          } else {
            overdueCount = e.overdue !== undefined ? e.overdue : (e.id % 3 === 0 ? 0 : e.id % 2 === 0 ? 2 : 1);
          }
          
          return (
            <div key={e.id} className="list-row tr-card" style={{ marginBottom: '8px', border: '1px solid var(--border-subtle)', padding: '0.9rem 1.1rem' }}>
              <Avatar who={e.name} size={34} />
              <div className="flex-1 min-w-0 text-left flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm" style={{ width: '120px' }}>{e.name}</span>
                  <Tag tone="neutral">{e.team || 'Unassigned'}</Tag>
                  {overdueCount > 0 && <Tag tone="danger"><span className="dot" style={{ background: '#ef4444', width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }} /> {overdueCount} overdue</Tag>}
                </div>
                
                <div className="flex items-center gap-4 text-xs text-muted text-right">
                  <span>{completedCount} completed · {outstandingCount} outstanding · Manager {e.manager || "—"}</span>
                  <a href="#" className="source-link" onClick={(evt) => evt.preventDefault()}>
                    Moodle <Icon name="ext" size={11} />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function RequestsView({ requests }) {
  if (!requests?.length) return <div className="tr-card"><div className="tr-empty">No training requests found.</div></div>;
  const stageTone = { New: 'warn', Planned: 'warn', 'In progress': 'accent', 'Session booked': 'accent', Delivered: 'neutral', Feedback: 'neutral', Complete: 'green' };
  
  return (
    <div className="list-shell">
      {requests.map((r) => (
        <div key={r.id} className="list-row tr-card" style={{ marginBottom: '8px', border: '1px solid var(--border-subtle)', padding: '0.9rem 1.1rem' }}>
          <span className="row-icon"><Icon name="message" size={18} /></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-muted">{r.id || "REQ"}</span>
              <span className="font-semibold text-sm truncate">{r.title || r.name}</span>
              {r.stage && <Tag tone={stageTone[r.stage] || 'neutral'}>{r.stage}</Tag>}
            </div>
            <p className="text-xs text-muted mt-1">{r.team || r.requester || "—"}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuthoringView({ authoring }) {
  if (!authoring?.length) return <div className="tr-card"><div className="tr-empty">No authoring courses found.</div></div>;
  const AUTH_STAGES = ['Script', 'Material', 'Moodle', 'Quiz', 'Deadline', 'Published'];
  return (
    <div className="list-shell">
      {authoring.map((c) => {
        const stageIndex = AUTH_STAGES.indexOf(c.stage || 'Script');
        return (
          <div key={c.id} className="list-row tr-card" style={{ marginBottom: '8px', border: '1px solid var(--border-subtle)', padding: '0.9rem 1.1rem' }}>
            <span className={`row-icon ${c.stage === 'Published' ? 'row-icon-pub' : ''}`}><Icon name="pen" size={18} /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{c.name || c.title}</span>
                <Tag tone="neutral">{c.id || "AUTH"}</Tag>
                {c.stage === 'Published' ? <Tag tone="green" icon="check">Published</Tag> : <Tag tone="accent">In progress</Tag>}
              </div>
              <ol className="stepper mt-1">
                {AUTH_STAGES.map((s, i) => (
                  <React.Fragment key={s}>
                    <li className={`step ${i < stageIndex ? 'step-done' : i === stageIndex ? 'step-cur' : ''}`}>
                      {i < stageIndex ? <Icon name="check" size={12} /> : i === stageIndex ? <span className="step-dot step-dot-cur" /> : <span className="step-dot step-dot-todo" />}
                      {s}
                    </li>
                    {i < AUTH_STAGES.length - 1 && <span className={`step-line ${i < stageIndex ? 'step-line-done' : ''}`} />}
                  </React.Fragment>
                ))}
              </ol>
            </div>
            <div className="flex flex-col items-end justify-center ml-4 shrink-0 gap-1">
              <div className="flex items-center gap-2 text-xs text-muted">
                <Avatar who={c.author === 'cait' ? 'Cait Burton' : 'Dev Sharma'} size={24} />
                Target {c.target ? new Date(c.target).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KpiView({ kpis }) {
  if (!kpis || !Object.keys(kpis).length) return <div className="tr-card"><div className="tr-empty">No KPIs available.</div></div>;
  return (
    <div className="kpi-panel">
      <p className="text-xs text-muted mb-4" style={{ marginBottom: '16px' }}>Training numbers for the weekly meeting : each drawn from one owned, locked computation in the data-authority registry (FR-16).</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', paddingBottom: '32px' }}>
        {Object.values(kpis).map(kpi => (
          <div key={kpi.id} className="tr-card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <span className="text-xs font-semibold text-muted">{kpi.id}</span>
                {kpi.target && <Tag tone={kpi.targetTone} icon={kpi.targetTone === 'warn' ? 'alert' : ''}>{kpi.target}</Tag>}
              </div>
              <h3 className="font-semibold text-sm" style={{ marginBottom: '16px' }}>{kpi.name}</h3>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '12px' }}>
                <span style={{ fontSize: '1.875rem', fontWeight: 700, lineHeight: 1 }}>{kpi.value}</span>
                <span className="text-sm font-semibold">{kpi.unit}</span>
              </div>
              <div className="text-xs font-semibold text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px', color: 'var(--color-accent)' }}>
                <span style={{ fontSize: '12px' }}>↗</span> {kpi.sub}
              </div>
              <div className="text-xs text-muted" style={{ borderLeft: '2px solid var(--border-subtle)', paddingLeft: '8px', marginBottom: '16px', opacity: 0.8 }}>
                <span style={{ fontSize: '12px', fontStyle: 'italic', marginRight: '4px' }}>f(x)</span> {kpi.formula}
              </div>
            </div>
            
            <div className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)', paddingTop: '12px', marginTop: '16px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Icon name="users" size={12}/> {kpi.owner}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border-subtle)', borderRadius: '4px', padding: '2px 6px', background: 'var(--bg-glass)' }}>
                {kpi.source} {kpi.lockHash ? <span style={{ opacity: 0.5 }}>#{kpi.lockHash}</span> : null}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditView({ audit }) {
  if (!audit?.length) return <div className="tr-card"><div className="tr-empty">No audit logs found.</div></div>;
  return (
    <div className="list-shell">
      {audit.map((a) => (
        <div key={a.id} className="list-row tr-card" style={{ marginBottom: '8px', border: '1px solid var(--border-subtle)', padding: '0.9rem 1.1rem' }}>
          <span className="row-icon"><Icon name="history" size={16} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm"><span className="font-semibold">{a.who}</span> {a.what}</p>
            <p className="text-xs text-muted mt-1">{a.when_time}</p>
          </div>
          {a.tag && <Tag tone="neutral">{a.tag}</Tag>}
        </div>
      ))}
    </div>
  );
}
